/* bz-killer-core.js — market-agnostic killer-LP behaviours core.
   Accordion + self-check (symptom checker) + animated stat counters + reading progress
   + buyer's-checklist progress (T-fecd05, ported from the UK monolith — copyless).
   ZERO market strings: every piece of copy comes from the section's DOM data-attributes,
   so this file is byte-identical across markets (US/DE/FR/UK copy it verbatim).

   WHY this exists: these behaviours used to live only in adhd-supplements-guide.js, which
   ALSO carries the UK-currency shelf renderer — so that whole file is UK-only (loading it on
   FR/DE/US clobbers the localized shelf). This core extracts the agnostic behaviours so
   non-UK killer pages get them WITHOUT the shelf renderer. UK keeps using guide.js.
   Load via {% render 'bz-killer-core' %} from any non-UK killer section (independent of
   the carousel loader). Idempotent — safe if loaded twice. The bespoke carousels + glue
   (sticky / close-shelf / stars) live separately in bz-carousel-init.js.
   Canon: reference/it-sops/killer-landing-page-build.md §3.3 / §18.
*/
(function () {
  'use strict';

  // ── Accordion (global for inline onclick; define only if absent so a legacy
  //    page script that already declared it is not clobbered) ──────────────────
  if (typeof window.toggleAccordion !== 'function') {
    window.toggleAccordion = function (btn) {
      var expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      var panel = btn.nextElementSibling;
      var item = btn.closest('.accordion-item');
      var chev = btn.querySelector('.accordion-chevron');
      if (expanded) {
        if (panel) panel.style.maxHeight = null;
        if (chev) chev.textContent = '+';
        if (item) item.classList.remove('open');
      } else {
        if (panel) panel.style.maxHeight = panel.scrollHeight + 'px';
        if (chev) chev.textContent = '−';
        if (item) item.classList.add('open');
      }
    };
  }

  // ── Self-check / symptom checker — result copy from DOM data-attributes ──────
  //    #symptomResult carries data-msg-low / data-msg-mid / data-msg-high.
  //    '{n}'    → the live checked count.
  //    '{total}' → the number of .sign-item.selectable on the page (per-page sign
  //               count, so the denominator is never hardcoded — killer-adhd=7, others vary).
  //    No hardcoded strings here.
  function initSelfCheck() {
    var items = document.querySelectorAll('.sign-item.selectable');
    var countEl = document.getElementById('symptomCount');
    if (!items.length || !countEl) return;
    var resultEl = document.getElementById('symptomResult');
    // T-fecd05 (4-peer QC): bind ONLY on a fully CORE-READY page — #symptomResult must carry all
    // three data-msg bands. Makes core immune to a monolith init failure on a non-migrated page, and
    // a partial/typo'd attr set leaves the page to the monolith rather than silently blanking a band.
    if (!resultEl || !resultEl.dataset.msgLow || !resultEl.dataset.msgMid || !resultEl.dataset.msgHigh) return;
    if (countEl.dataset.scBound) return;            // idempotent
    countEl.dataset.scBound = '1';
    var total = items.length;
    function fill(text, count) { return text.split('{n}').join(count).split('{total}').join(total); }
    function band(n) { return n <= 2 ? 'low' : (n <= 4 ? 'mid' : 'high'); }
    function toggleItem(item) {
      item.classList.toggle('checked');
      item.setAttribute('aria-checked', item.classList.contains('checked') ? 'true' : 'false');
      var count = document.querySelectorAll('.sign-item.checked').length;
      countEl.textContent = count;
      countEl.style.transform = 'scale(1.3)';
      setTimeout(function () { countEl.style.transform = 'scale(1)'; }, 200);
      if (!resultEl) return;
      resultEl.className = 'symptom-result';
      if (count === 0) { resultEl.classList.remove('visible'); return; }
      var b = band(count);
      var key = b === 'low' ? 'msgLow' : (b === 'mid' ? 'msgMid' : 'msgHigh');
      var text = resultEl.dataset[key];
      if (text) {
        resultEl.textContent = fill(text, count);
        resultEl.classList.add('visible', b === 'mid' ? 'medium' : b);
      }
    }
    items.forEach(function (item) {
      item.addEventListener('click', function () { toggleItem(this); });
      item.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleItem(this); }
      });
    });
  }

  // ── Animated stat counters (locale-decimal aware for FR/DE) ──────────────────
  //    data-target is ALWAYS dot-decimal (parses cleanly); data-decimal-sep (e.g. ',')
  //    is applied ONLY at the render step. An inner <span> suffix is preserved.
  function initCounters() {
    var stats = document.querySelectorAll('.stat-card .big[data-target]');
    if (!stats.length) return;
    var grid = document.querySelector('.stat-grid');
    if (!grid || grid.dataset.cuBound) return;      // idempotent
    grid.dataset.cuBound = '1';
    var done = false;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting || done) return;
        done = true;
        stats.forEach(function (el) {
          var target = parseFloat(el.dataset.target);            // dot-decimal source
          if (isNaN(target)) return;
          var sep = el.dataset.decimalSep || '.';
          var suffix = el.querySelector('span') ? el.querySelector('span').outerHTML : '';
          var decimals = target % 1 === 0 ? 0 : (el.dataset.target.split('.')[1] || '').length;
          var start = performance.now();
          (function animate(now) {
            var progress = Math.min((now - start) / 1200, 1);
            var eased = 1 - Math.pow(1 - progress, 3);
            var current = (target * eased).toFixed(decimals);
            if (sep !== '.') current = current.replace('.', sep);  // render-only locale sep
            el.innerHTML = current + suffix;
            if (progress < 1) requestAnimationFrame(animate);
          })(start);
        });
      });
    }, { threshold: 0.3 });
    observer.observe(grid);
  }

  // ── Buyer's-checklist progress tracker (T-fecd05 port — COPYLESS) ────────────
  //    Tracks which .guide-checklist li[data-check] have scrolled into view and fills
  //    matching .check-dot[data-check]. The label (#checkLabel) copy is read from its
  //    data-template ('{n}' = seen count, '{total}' = number of dots) so no English/
  //    fixed-total string lives in this agnostic core. Falls back to '{n}/{total}'.
  //    No-op where the checklist markup is absent (i.e. everywhere off-UK today).
  function initChecklist() {
    var items = document.querySelectorAll('.guide-checklist li[data-check]');
    var dots = document.querySelectorAll('.check-dot[data-check]');
    if (!items.length || !dots.length) return;
    var anchor = dots[0].parentElement || dots[0];
    if (anchor.dataset.clBound) return;             // idempotent (shared with hardened monolith)
    anchor.dataset.clBound = '1';
    var label = document.getElementById('checkLabel');
    var tpl = (label && label.dataset.template) ? label.dataset.template : '{n}/{total}';
    var total = dots.length;
    var seen = {};
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        seen[entry.target.dataset.check] = 1;
        dots.forEach(function (dot) { if (seen[dot.dataset.check]) dot.classList.add('seen'); });
        if (label) label.textContent = tpl.split('{n}').join(Object.keys(seen).length).split('{total}').join(total);
      });
    }, { threshold: 0.6 });
    items.forEach(function (item) { observer.observe(item); });
  }

  // ── Plan-your-path formula mini-quiz (inline in router branch C; formula-only) ──
  //    Scope-queried per [data-planpath] root (NEVER page-global ids), so multiple
  //    instances can't collide. ALL copy + product data come from the root's DOM
  //    (.pp-quiz-formulas [data-formula] data-attrs), so this stays byte-identical
  //    across markets. No layout-measuring → safe to bind while the panel is display:none.
  function initPlanpath() {
    var roots = document.querySelectorAll('[data-planpath]');
    if (!roots.length) return;
    roots.forEach(function (root) {
      if (root.dataset.ppBound) return;             // idempotent + double-bind guard
      root.dataset.ppBound = '1';

      // optional tabs within this root (formula-only variant has none — guarded)
      var tabs = root.querySelectorAll('.planpath-tab');
      var panels = root.querySelectorAll('.planpath-panel');
      tabs.forEach(function (t) {
        t.addEventListener('click', function () {
          var key = t.getAttribute('data-tab');
          tabs.forEach(function (x) { var on = x === t; x.classList.toggle('active', on); x.setAttribute('aria-selected', on ? 'true' : 'false'); });
          panels.forEach(function (p) { p.classList.toggle('active', p.getAttribute('data-panel') === key); });
        });
      });

      // optional wait/NHS lookup (data-driven; absent on markets w/ a static node-1 → guarded)
      var waitSel = root.querySelector('.pp-nhs-select');
      if (waitSel) {
        waitSel.addEventListener('change', function () {
          var opt = waitSel.options[waitSel.selectedIndex];
          var range = opt ? opt.getAttribute('data-range') : '';
          var tip = opt ? opt.getAttribute('data-tip') : '';
          var rEl = root.querySelector('.pp-nhs-range'); if (rEl) rEl.innerHTML = range || '&mdash;';
          var tEl = root.querySelector('.pp-nhs-tip'); if (tEl) tEl.innerHTML = tip || '&mdash;';
          var resEl = root.querySelector('.pp-nhs-result'); if (resEl) resEl.classList.toggle('visible', !!waitSel.value);  // T-fecd05 fix: reveal the result box (CSS .pp-nhs-result is display:none until .visible)
        });
      }

      var stepper = root.querySelector('.pp-quiz-stepper');
      if (!stepper) return;
      var track = stepper.querySelector('.pp-quiz-track');
      if (!track) return;
      var votes = {};

      function formula(key) {
        var el = root.querySelector('.pp-quiz-formulas [data-formula="' + key + '"]');
        return el ? { name: el.dataset.name, blurb: el.dataset.blurb, cta: el.dataset.cta, url: el.dataset.url } : null;
      }
      function goToSlide(n) {
        track.querySelectorAll('.pp-quiz-slide').forEach(function (s) {
          var step = parseInt(s.getAttribute('data-step'), 10);
          var isTarget = (step === n + 1);
          s.style.display = isTarget ? '' : 'none';
          s.classList.toggle('active', isTarget);
        });
        if (n >= 3) showResult();
      }
      function showResult() {
        var score = { original: 0, pro: 0, elite: 0 };
        Object.keys(votes).forEach(function (k) { if (score[votes[k]] != null) score[votes[k]]++; });
        var winner = 'pro';                          // tie-break: elite > pro > original
        if (score.elite >= score.pro && score.elite >= score.original) winner = 'elite';
        else if (score.original >= score.pro) winner = 'original';
        var f = formula(winner);
        if (!f) return;
        var nameEl = root.querySelector('.pp-quiz-name'); if (nameEl) nameEl.textContent = f.name;
        var blurbEl = root.querySelector('.pp-quiz-blurb'); if (blurbEl) blurbEl.textContent = f.blurb;
        var ctaEl = root.querySelector('.pp-quiz-cta'); if (ctaEl) { ctaEl.textContent = f.cta; ctaEl.href = f.url; }
        var rs = root.querySelector('.pp-quiz-result-slide'); if (rs) rs.style.display = '';
      }

      goToSlide(0);
      var advancing = false;
      stepper.querySelectorAll('input[type="radio"]').forEach(function (radio) {
        radio.addEventListener('change', function () {
          stepper.querySelectorAll('input[name="' + radio.name + '"]').forEach(function (r) {
            var opt = r.closest('.pp-quiz-option'); if (opt) opt.classList.toggle('selected', r.checked);
          });
          votes[radio.name] = radio.value;
          var slide = radio.closest('.pp-quiz-slide');
          var targetSlide = slide ? parseInt(slide.getAttribute('data-step'), 10) : 0;
          if (!advancing) { advancing = true; setTimeout(function () { advancing = false; goToSlide(targetSlide); }, 280); }
        });
      });
      var resetBtn = root.querySelector('.pp-quiz-reset');
      if (resetBtn) {
        resetBtn.addEventListener('click', function () {
          votes = {};
          stepper.querySelectorAll('input[type="radio"]').forEach(function (r) {
            r.checked = false; var opt = r.closest('.pp-quiz-option'); if (opt) opt.classList.remove('selected');
          });
          goToSlide(0);
        });
      }
    });
  }

  // ── Reading progress bar (optional — only if #progressBar is present) ────────
  function initProgress() {
    var bar = document.getElementById('progressBar');
    if (!bar || bar.dataset.pbBound) return;        // idempotent
    bar.dataset.pbBound = '1';
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var h = document.documentElement.scrollHeight - window.innerHeight;
        var pct = h > 0 ? (window.scrollY / h) * 100 : 0;
        bar.style.width = Math.max(0, Math.min(100, pct)) + '%';
        ticking = false;
      });
    }, { passive: true });
  }

  function boot() { initSelfCheck(); initCounters(); initChecklist(); initPlanpath(); initProgress(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
